import { useCallback } from "react";
import html2canvas from "html2canvas";
//@ts-ignore
import { jsPDF } from "jspdf";

export const WidthH4 = 900;
export const HeightH4 = 1070;

const textInputTypes = new Set([
  "date",
  "datetime-local",
  "email",
  "month",
  "number",
  "password",
  "search",
  "tel",
  "text",
  "time",
  "url",
  "week",
]);

const replaceTextControls = (document: Document, element: HTMLElement) => {
  const controls = element.querySelectorAll("input, textarea");

  controls.forEach((control) => {
    if (control.getAttribute("aria-hidden") === "true") {
      return;
    }

    const isTextarea = control.tagName === "TEXTAREA";
    const isTextInput =
      control.tagName === "INPUT" &&
      textInputTypes.has((control as HTMLInputElement).type);

    if (!isTextarea && !isTextInput) {
      return;
    }

    const replacement = document.createElement("div");
    const computedStyle = document.defaultView?.getComputedStyle(control);
    const inputContainer = control.parentElement;
    const containerStyle = inputContainer
      ? document.defaultView?.getComputedStyle(inputContainer)
      : undefined;

    if (computedStyle) {
      [
        "color",
        "display",
        "font-family",
        "font-size",
        "font-style",
        "font-weight",
        "letter-spacing",
        "line-height",
        "margin-bottom",
        "margin-left",
        "margin-right",
        "margin-top",
        "padding-bottom",
        "padding-left",
        "padding-right",
        "padding-top",
        "text-align",
        "text-decoration",
        "text-transform",
        "vertical-align",
      ].forEach((property) => {
        replacement.style.setProperty(
          property,
          computedStyle.getPropertyValue(property)
        );
      });

      const isMaterialInput =
        inputContainer?.classList.contains("MuiInputBase-root");
      const containerWidth = inputContainer?.getBoundingClientRect().width ?? 0;
      const horizontalPadding = containerStyle
        ? parseFloat(containerStyle.paddingLeft || "0") +
          parseFloat(containerStyle.paddingRight || "0")
        : 0;

      const controlBounds = control.getBoundingClientRect();
      const availableWidth = containerWidth - horizontalPadding;
      replacement.style.width = `${
        isMaterialInput && availableWidth > 0
          ? availableWidth
          : controlBounds.width
      }px`;
      replacement.style.minHeight = `${controlBounds.height}px`;
    }

    const textControl = control as HTMLInputElement | HTMLTextAreaElement;
    replacement.className = control.className;
    replacement.textContent =
      textControl.value || control.getAttribute("placeholder") || "";
    replacement.style.boxSizing = "border-box";
    replacement.style.flex = "1 1 auto";
    replacement.style.minWidth = "0";
    replacement.style.maxWidth = "100%";
    replacement.style.height = "auto";
    replacement.style.maxHeight = "none";
    replacement.style.overflow = isTextarea ? "visible" : "hidden";
    replacement.style.overflowWrap = isTextarea ? "anywhere" : "normal";
    replacement.style.whiteSpace = isTextarea ? "pre-wrap" : "pre";
    control.parentNode?.replaceChild(replacement, control);
  });
};

export const useGenerateElementPDF = () => {
  const generateElementPDF = useCallback(
    (element: HTMLElement | null, fileName: string) => {
      return new Promise<void>((resolve, reject) => {
        if (!element) {
          reject("element not found");
          return;
        }
        html2canvas(element, {
          onclone: replaceTextControls,
          scale: 1,
        })
          .then((canvas) => {
            const pdf = new jsPDF("p", "pt", "letter");
            const pageCount = Math.max(1, Math.ceil(canvas.height / HeightH4));
            const sourceWidth = Math.min(WidthH4, canvas.width);

            for (let i = 0; i < pageCount; i++) {
              const sX = 0;
              const sY = HeightH4 * i;
              const sWidth = sourceWidth;
              const sHeight = Math.min(HeightH4, canvas.height - sY);
              const dX = 0;
              const dY = 0;
              const dWidth = sWidth;
              const dHeight = sHeight;

              const onePageCanvas = document.createElement("canvas");
              onePageCanvas.width = dWidth;
              onePageCanvas.height = dHeight;
              const ctx = onePageCanvas.getContext("2d");
              if (!ctx) {
                reject("canvas context not found");
                return;
              }
              ctx.fillStyle = "#fff";
              ctx.fillRect(0, 0, dWidth, dHeight);
              ctx.drawImage(
                canvas,
                sX,
                sY,
                sWidth,
                sHeight,
                dX,
                dY,
                dWidth,
                dHeight
              );

              const canvasDataURL = onePageCanvas.toDataURL("image/png", 1.0);
              const width = onePageCanvas.width;
              const height = onePageCanvas.height;

              if (i > 0) {
                pdf.addPage([612, 792]);
              }

              pdf.setPage(i + 1);
              pdf.addImage(
                canvasDataURL,
                "PNG",
                20,
                40,
                width * 0.62,
                height * 0.62
              );
            }

            pdf.save(fileName || "");
            resolve();
          })
          .catch(reject);
      });
    },
    []
  );

  return [generateElementPDF] as [typeof generateElementPDF];
};
